import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Send, Trash2 } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, InlineAlert, Input, NativeSelect, Skeleton, Textarea, useSave } from "@ncp/ui";
import { useInvalidateRequests, type MinistryRequest, type OpinionDraft, type RequestItem } from "./api";
import { useProcedureTermWords } from "./terms";

/**
 * Asks a sector regulator or an emirate authority for its technical opinion on a file (Decree-Law, Article 20): the request is prefilled
 * from the file – the summary, the matter in question and three questions worded for the kind of file – and the bodies whose sectors the
 * file's markets fall in come first. On a concentration it is tied to the coordination entry, which it creates when there is none.
 */
export function OpinionRequestDialog({ caseId, open, onOpenChange, coordinationEntryId, bodyKey, onSent }: {
  caseId: string; open: boolean; onOpenChange: (o: boolean) => void; coordinationEntryId?: string | null; bodyKey?: string | null; onSent?: (r: MinistryRequest) => void;
}) {
  const { t } = useTranslation();
  // Conditions 2.13 and 2.14: the periods and provisions a request names, from their one definition and the legal rules register.
  const terms = useProcedureTermWords();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const api = useApi();
  const invalidate = useInvalidateRequests();
  const draft = useQuery({ queryKey: ["requests", "opinion-draft", caseId], queryFn: () => api.get<OpinionDraft>(`/api/requests/opinion-draft/${caseId}`), enabled: open });
  const d = draft.data;
  const [body, setBody] = useState("");
  const [entry, setEntry] = useState("");
  const [focalName, setFocalName] = useState("");
  const [focalEmail, setFocalEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [summary, setSummary] = useState("");
  const [context, setContext] = useState("");
  const [questions, setQuestions] = useState<RequestItem[]>([]);

  useEffect(() => {
    if (!open || !d) return;
    const fromEntry = coordinationEntryId ? d.coordination.find((c) => c.id === coordinationEntryId)?.bodyKey : null;
    const key = bodyKey ?? fromEntry ?? d.bodies.find((b) => b.suggested)?.key ?? "";
    setBody(key);
    setEntry(coordinationEntryId ?? d.coordination.find((c) => c.bodyKey === key)?.id ?? "");
    setFocalEmail(d.bodies.find((b) => b.key === key)?.focalPointEmail ?? "");
    setFocalName("");
    setSubject(ar ? d.subjectAr : d.subject);
    setSummary(ar ? d.summaryAr : d.summary);
    setContext(ar ? d.contextAr : d.context);
    setQuestions(d.questions);
  }, [open, d, coordinationEntryId, bodyKey, ar]);

  const pick = (key: string) => {
    setBody(key);
    setFocalEmail(d?.bodies.find((b) => b.key === key)?.focalPointEmail ?? "");
    setEntry(d?.coordination.find((c) => c.bodyKey === key)?.id ?? "");
  };

  const send = useSave({
    // The request issued comes back as stored: the texts the officer edited are confirmed by it.
    expect: "record",
    mutationFn: () => api.post<MinistryRequest>("/api/requests/opinions", {
      caseId, bodyKey: body, coordinationEntryId: entry || null, contactName: focalName || null, contactEmail: focalEmail || null,
      // The text the officer edited is sent in the language it was written in; the other language keeps the prefilled draft.
      subject: ar ? d!.subject : subject, subjectAr: ar ? subject : d!.subjectAr,
      summary: ar ? d!.summary : summary, summaryAr: ar ? summary : d!.summaryAr,
      context: ar ? d!.context : context, contextAr: ar ? context : d!.contextAr,
      questions: questions.map((q) => ({ ...q, label: q.label.trim() || q.labelAr || "" })),
    }),
    saved: t("backoffice:requests.newOpinion.sent"),
    onSuccess: (r) => { invalidate(); onSent?.(r); onOpenChange(false); },
  });
  // The officer's edits of the platform's draft are kept on the device per file and language until the request is issued (S 2.7).
  const draftOf = (field: string) => `case:${caseId}:opinion-request:${field}:${ar ? "ar" : "en"}`;

  const regulators = (d?.bodies ?? []).filter((b) => b.role === "SectoralRegulator");
  const authorities = (d?.bodies ?? []).filter((b) => b.role === "RelevantAuthority");
  const name = (b: { nameEn: string; nameAr: string }) => (ar ? b.nameAr : b.nameEn);
  const ready = !!body && focalEmail.includes("@") && subject.trim().length >= 5 && summary.trim().length >= 20 && context.trim().length >= 20 && questions.length > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("backoffice:requests.newOpinion.title")}</DialogTitle><DialogDescription>{t("backoffice:requests.newOpinion.description", terms)}</DialogDescription></DialogHeader>
        {!d ? <Skeleton className="h-80" /> : (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label={t("backoffice:requests.newOpinion.body")} required>
                {(fid) => (
                  <NativeSelect id={fid} value={body} onChange={(e) => pick(e.target.value)}>
                    <option value="">—</option>
                    {regulators.some((b) => b.suggested) ? <optgroup label={t("backoffice:requests.newOpinion.suggested")}>{regulators.filter((b) => b.suggested).map((b) => <option key={b.key} value={b.key}>{name(b)}</option>)}</optgroup> : null}
                    <optgroup label={t("backoffice:requests.newOpinion.regulators")}>{regulators.filter((b) => !b.suggested).map((b) => <option key={b.key} value={b.key}>{name(b)}</option>)}</optgroup>
                    <optgroup label={t("backoffice:requests.newOpinion.authorities")}>{authorities.map((b) => <option key={b.key} value={b.key}>{name(b)}</option>)}</optgroup>
                  </NativeSelect>
                )}
              </Field>
              {d.caseType === "EconomicConcentration" ? (
                <Field label={t("backoffice:requests.newOpinion.coordinationEntry")}>
                  {(fid) => (
                    <NativeSelect id={fid} value={entry} onChange={(e) => setEntry(e.target.value)}>
                      <option value="">{t("backoffice:requests.newOpinion.newEntry")}</option>
                      {d.coordination.map((c) => <option key={c.id} value={c.id}>{ar && c.authorityAr ? c.authorityAr : c.authority}</option>)}
                    </NativeSelect>
                  )}
                </Field>
              ) : null}
              <Field label={t("backoffice:requests.newOpinion.focalName")}>{(fid) => <Input id={fid} value={focalName} onChange={(e) => setFocalName(e.target.value)} dir="auto" />}</Field>
              <Field label={t("backoffice:requests.newOpinion.focalEmail")} hint={t("backoffice:requests.newOpinion.focalEmailHint")} required>{(fid) => <Input id={fid} type="email" value={focalEmail} onChange={(e) => setFocalEmail(e.target.value)} />}</Field>
            </div>
            <Field label={t("backoffice:requests.newOpinion.subject")} required>{(fid) => <Input id={fid} value={subject} onChange={(e) => setSubject(e.target.value)} dir="auto" />}</Field>
            <Field label={t("backoffice:requests.newOpinion.summary")} required>{(fid) => <Textarea id={fid} rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} draft={draftOf("summary")} dir="auto" />}</Field>
            <Field label={t("backoffice:requests.newOpinion.context")} required>{(fid) => <Textarea id={fid} rows={4} value={context} onChange={(e) => setContext(e.target.value)} draft={draftOf("context")} dir="auto" />}</Field>
            <section>
              <p className="mb-2 text-sm font-semibold">{t("backoffice:requests.newOpinion.questions")}</p>
              <ol className="space-y-2">
                {questions.map((q, i) => (
                  <li key={q.key} className="flex items-start gap-2">
                    <span className="mt-2 w-5 text-end text-xs tabular-nums text-muted-foreground">{i + 1}.</span>
                    <Textarea rows={2} className="flex-1" draft={draftOf(`question:${q.key}`)} value={ar && q.labelAr ? q.labelAr : q.label} onChange={(e) => setQuestions((xs) => xs.map((x) => (x.key === q.key ? (ar ? { ...x, labelAr: e.target.value } : { ...x, label: e.target.value }) : x)))} aria-label={t("backoffice:requests.newOpinion.questions")} dir="auto" />
                    <Button size="icon" variant="ghost" className="size-8" onClick={() => setQuestions((xs) => xs.filter((x) => x.key !== q.key))} aria-label={t("actions.delete")}><Trash2 className="size-3.5" /></Button>
                  </li>
                ))}
              </ol>
              <Button size="sm" variant="ghost" className="mt-1" onClick={() => setQuestions((xs) => [...xs, { key: `q${xs.length + 1}-${Date.now() % 10000}`, kind: "Text", label: "", labelAr: null, required: true, years: [] }])}><Plus /> {t("backoffice:requests.newOpinion.addQuestion")}</Button>
            </section>
            <p className="text-xs text-muted-foreground">{t("backoffice:requests.newOpinion.dueOn", { ...terms, date: formatDate(d.dueOn, locale) })}</p>
            {d.caseType === "EconomicConcentration" ? <InlineAlert tone="info">{t("backoffice:requests.newOpinion.interruptsClock")}</InlineAlert> : null}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("actions.cancel")}</Button>
          <Button variant="gold" primary disabled={!ready} loading={send.isPending} onClick={() => send.mutate()}><Send /> {t("backoffice:requests.newOpinion.send")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
